import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus, CheckCircle2, Circle, Clock, AlertTriangle, Loader2,
  X, Filter, Users, Store, ChevronDown, ChevronRight,
  MessageSquare, Repeat, Trash2, CalendarDays, Tag,
  Target, TrendingUp, User, FileText, Play, Copy, Pencil, GripVertical,
  ArrowUpDown
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_CONFIG = {
  urgent: { label: "Urgent", color: "bg-red-100 text-red-700 border-red-200", dot: "bg-red-500" },
  high: { label: "High", color: "bg-orange-100 text-orange-700 border-orange-200", dot: "bg-orange-500" },
  medium: { label: "Medium", color: "bg-blue-100 text-blue-700 border-blue-200", dot: "bg-blue-500" },
  low: { label: "Low", color: "bg-stone-100 text-stone-600 border-stone-200", dot: "bg-stone-400" },
};

const STATUS_CONFIG = {
  pending: { label: "To Do", icon: Circle, color: "text-stone-400" },
  in_progress: { label: "In Progress", icon: Clock, color: "text-blue-500" },
  completed: { label: "Done", icon: CheckCircle2, color: "text-emerald-500" },
};

const RECURRENCE_LABELS = { daily: "Daily", weekly: "Weekly", monthly: "Monthly" };

// --- Sub-components ---

function StatCard({ label, value, icon: Icon, color = "text-stone-900" }) {
  return (
    <div className="bg-white border border-stone-200/60 rounded-xl p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-stone-100">
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <div>
        <p className="text-2xl font-bold text-stone-900">{value}</p>
        <p className="text-xs text-stone-500">{label}</p>
      </div>
    </div>
  );
}

function MemberProgress({ stats }) {
  if (!stats || stats.length === 0) return null;
  return (
    <Card className="border-stone-200/60">
      <CardContent className="p-5">
        <h3 className="text-sm font-semibold text-stone-800 mb-4 flex items-center gap-2">
          <Users className="w-4 h-4" /> Team Progress
        </h3>
        <div className="space-y-3">
          {stats.map((m) => (
            <div key={m.user_id} className="flex items-center gap-3">
              <div className="w-7 h-7 bg-stone-200 rounded-full flex items-center justify-center text-xs font-bold text-stone-600">
                {m.name?.charAt(0)?.toUpperCase() || "?"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-stone-700 truncate">{m.name}</span>
                  <span className="text-xs text-stone-500">{m.completed}/{m.total}</span>
                </div>
                <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${m.completion_rate}%` }} />
                </div>
              </div>
              {m.overdue > 0 && <span className="text-xs text-red-500 font-medium">{m.overdue} overdue</span>}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function TaskCard({ task, stores, members, categories, onUpdate, onDelete, onAddNote, onComplete, isOwner, onEdit }) {
  const [expanded, setExpanded] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const pri = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
  const st = STATUS_CONFIG[task.status] || STATUS_CONFIG.pending;
  const StatusIcon = st.icon;
  const storeName = stores.find(s => s.id === task.store_id)?.name;
  const assigneeName = members.find(m => m.id === task.assigned_to)?.name;
  const categoryObj = categories.find(c => c.id === task.category_id);
  const isOverdue = task.due_date && task.status !== "completed" && new Date(task.due_date) < new Date();

  const handleNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    await onAddNote(task.id, noteText.trim());
    setNoteText("");
    setAddingNote(false);
  };

  return (
    <div data-testid={`task-card-${task.id}`}
      className={`bg-white border rounded-xl transition-all hover:shadow-sm ${isOverdue ? "border-red-200 bg-red-50/30" : "border-stone-200/60"} ${task.status === "completed" ? "opacity-70" : ""}`}>
      <div className="p-4 flex items-start gap-3">
        <button data-testid={`task-toggle-${task.id}`}
          onClick={() => task.status === "completed" ? onUpdate(task.id, { status: "pending" }) : onComplete(task.id)}
          className="mt-0.5 flex-shrink-0">
          <StatusIcon className={`w-5 h-5 ${st.color} hover:scale-110 transition-transform`} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <p className={`text-sm font-medium ${task.status === "completed" ? "line-through text-stone-400" : "text-stone-900"}`}>{task.title}</p>
              {task.description && !expanded && <p className="text-xs text-stone-500 mt-0.5 truncate">{task.description}</p>}
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${pri.color}`}>{pri.label}</span>
              {task.recurring !== "none" && (
                <span className="px-1.5 py-0.5 text-[10px] font-medium bg-violet-100 text-violet-700 rounded-full flex items-center gap-0.5">
                  <Repeat className="w-2.5 h-2.5" /> {task.recurring}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            {storeName && <span className="text-[11px] text-stone-500 flex items-center gap-1"><Store className="w-3 h-3" /> {storeName}</span>}
            {assigneeName && <span className="text-[11px] text-stone-500 flex items-center gap-1"><User className="w-3 h-3" /> {assigneeName}</span>}
            {categoryObj && <span className="text-[11px] flex items-center gap-1" style={{ color: categoryObj.color }}><Tag className="w-3 h-3" /> {categoryObj.name}</span>}
            {task.due_date && (
              <span className={`text-[11px] flex items-center gap-1 ${isOverdue ? "text-red-500 font-medium" : "text-stone-500"}`}>
                <CalendarDays className="w-3 h-3" /> {new Date(task.due_date).toLocaleDateString()}{isOverdue && " (overdue)"}
              </span>
            )}
            {task.template_name && <span className="text-[11px] text-violet-500 flex items-center gap-1"><FileText className="w-3 h-3" /> {task.template_name}</span>}
            {task.notes?.length > 0 && <span className="text-[11px] text-stone-400 flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {task.notes.length}</span>}
            <button onClick={() => setExpanded(!expanded)} className="text-[11px] text-stone-400 hover:text-stone-600 ml-auto">
              {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-4 pt-0 border-t border-stone-100 space-y-3">
          {task.description && <p className="text-sm text-stone-600 mt-3">{task.description}</p>}
          <div className="flex items-center gap-2 mt-3">
            {["pending", "in_progress", "completed"].map(s => (
              <button key={s} onClick={() => s === "completed" ? onComplete(task.id) : onUpdate(task.id, { status: s })}
                className={`text-xs px-3 py-1 rounded-full border transition-all ${task.status === s ? "bg-stone-900 text-white border-stone-900" : "border-stone-200 text-stone-600 hover:bg-stone-50"}`}>
                {STATUS_CONFIG[s].label}
              </button>
            ))}
            {(isOwner || task.created_by === task.assigned_to) && (
              <div className="ml-auto flex items-center gap-1">
                <button data-testid={`task-edit-${task.id}`} onClick={() => onEdit(task)} className="text-xs text-stone-400 hover:text-stone-600 p-1"><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => onDelete(task.id)} className="text-xs text-red-400 hover:text-red-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            )}
          </div>
          {task.notes?.length > 0 && (
            <div className="space-y-2 mt-2">
              <p className="text-xs font-semibold text-stone-600">Notes</p>
              {task.notes.map((n) => (
                <div key={n.id} className="p-2 bg-stone-50 rounded-lg text-xs">
                  <span className="font-medium text-stone-700">{n.user_name}</span>
                  <span className="text-stone-400 ml-2">{new Date(n.created_at).toLocaleDateString()}</span>
                  <p className="text-stone-600 mt-1">{n.text}</p>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2 mt-2">
            <Input value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note..." className="text-xs h-8"
              onKeyDown={e => e.key === "Enter" && handleNote()} />
            <Button size="sm" variant="outline" onClick={handleNote} disabled={addingNote || !noteText.trim()} className="h-8 text-xs">
              {addingNote ? <Loader2 className="w-3 h-3 animate-spin" /> : "Add"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function CreateTaskForm({ stores, members, categories, onSubmit, onCancel, initialData = null }) {
  const [form, setForm] = useState({
    title: initialData?.title || "", description: initialData?.description || "",
    store_id: initialData?.store_id || "", assigned_to: initialData?.assigned_to || "",
    category_id: initialData?.category_id || "", priority: initialData?.priority || "medium",
    due_date: initialData?.due_date ? initialData.due_date.slice(0, 10) : "",
    recurring: initialData?.recurring || "none", recurring_end_date: initialData?.recurring_end_date || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const isEditing = !!initialData;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title is required");
    setSubmitting(true);
    const payload = { ...form };
    if (!payload.store_id) payload.store_id = null;
    if (!payload.assigned_to) payload.assigned_to = null;
    if (!payload.category_id) payload.category_id = null;
    if (!payload.due_date) payload.due_date = null;
    if (!payload.recurring_end_date) payload.recurring_end_date = null;
    await onSubmit(payload);
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} data-testid="create-task-form" className="bg-white border border-stone-200/60 rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-stone-800">{isEditing ? "Edit Task" : "New Task"}</h3>
        <button type="button" onClick={onCancel}><X className="w-4 h-4 text-stone-400" /></button>
      </div>
      <Input data-testid="task-title-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Task title" className="text-sm" />
      <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Description (optional)" className="text-sm min-h-[60px]" />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] text-stone-500 font-medium mb-1 block">Store</label>
          <select data-testid="task-store-select" value={form.store_id} onChange={e => setForm(f => ({ ...f, store_id: e.target.value }))}
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="">Company-wide</option>
            {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] text-stone-500 font-medium mb-1 block">Assign To</label>
          <select data-testid="task-assign-select" value={form.assigned_to} onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))}
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="">Myself</option>
            {members.map(m => <option key={m.id} value={m.id}>{m.name || m.email}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] text-stone-500 font-medium mb-1 block">Category</label>
          <select value={form.category_id} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="">None</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] text-stone-500 font-medium mb-1 block">Priority</label>
          <select data-testid="task-priority-select" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
        <div>
          <label className="text-[11px] text-stone-500 font-medium mb-1 block">Due Date</label>
          <Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} className="text-sm" />
        </div>
        <div>
          <label className="text-[11px] text-stone-500 font-medium mb-1 block">Recurring</label>
          <select data-testid="task-recurring-select" value={form.recurring} onChange={e => setForm(f => ({ ...f, recurring: e.target.value }))}
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="none">None</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
      </div>
      {form.recurring !== "none" && (
        <div>
          <label className="text-[11px] text-stone-500 font-medium mb-1 block">Recurring End Date</label>
          <Input type="date" value={form.recurring_end_date} onChange={e => setForm(f => ({ ...f, recurring_end_date: e.target.value }))} className="text-sm w-48" />
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={submitting} className="bg-stone-900 text-white hover:bg-stone-800" data-testid="submit-task-btn">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : isEditing ? <Pencil className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />} {isEditing ? "Update Task" : "Create Task"}
        </Button>
      </div>
    </form>
  );
}

function CategoryManager({ categories, onAdd, onDelete }) {
  const [name, setName] = useState("");
  const [color, setColor] = useState("#6b7280");
  const [showForm, setShowForm] = useState(false);

  const handleAdd = async () => {
    if (!name.trim()) return;
    await onAdd({ name: name.trim(), color });
    setName(""); setColor("#6b7280"); setShowForm(false);
  };

  return (
    <Card className="border-stone-200/60">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-stone-800 flex items-center gap-2"><Tag className="w-4 h-4" /> Categories</h3>
          <Button size="sm" variant="ghost" onClick={() => setShowForm(!showForm)} className="text-xs h-7"><Plus className="w-3 h-3 mr-1" /> Add</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {categories.map(c => (
            <div key={c.id} className="flex items-center gap-1.5 px-2.5 py-1 bg-stone-50 rounded-full border border-stone-200 group">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color }} />
              <span className="text-xs text-stone-700">{c.name}</span>
              <button onClick={() => onDelete(c.id)} className="opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="w-3 h-3 text-stone-400 hover:text-red-500" />
              </button>
            </div>
          ))}
          {categories.length === 0 && <p className="text-xs text-stone-400">No categories yet</p>}
        </div>
        {showForm && (
          <div className="flex gap-2 items-end mt-3 pt-3 border-t border-stone-100">
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Category name" className="text-xs h-8 flex-1"
              onKeyDown={e => e.key === "Enter" && handleAdd()} />
            <input type="color" value={color} onChange={e => setColor(e.target.value)} className="w-8 h-8 rounded cursor-pointer border-0" />
            <Button size="sm" onClick={handleAdd} className="h-8 text-xs bg-stone-900 text-white"><Plus className="w-3 h-3" /></Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// --- Template Components ---

function TemplateCard({ template, stores, onApply, onEdit, onDelete }) {
  const [showApply, setShowApply] = useState(false);
  const [selectedStores, setSelectedStores] = useState([]);
  const [applying, setApplying] = useState(false);

  const handleApply = async () => {
    if (selectedStores.length === 0) return toast.error("Select at least one store");
    setApplying(true);
    await onApply(template.id, selectedStores);
    setApplying(false);
    setShowApply(false);
    setSelectedStores([]);
  };

  const toggleStore = (sid) => {
    setSelectedStores(prev => prev.includes(sid) ? prev.filter(s => s !== sid) : [...prev, sid]);
  };

  return (
    <div data-testid={`template-card-${template.id}`} className="bg-white border border-stone-200/60 rounded-xl p-5 transition-all hover:shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="w-4 h-4 text-violet-500" />
            <h4 className="text-sm font-semibold text-stone-900">{template.name}</h4>
            <span className="px-2 py-0.5 text-[10px] font-medium bg-violet-100 text-violet-700 rounded-full">
              {RECURRENCE_LABELS[template.recurring] || template.recurring}
            </span>
          </div>
          {template.description && <p className="text-xs text-stone-500 mb-3">{template.description}</p>}
          <div className="space-y-1.5">
            {template.items?.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 text-xs text-stone-600">
                <div className="w-4 h-4 rounded-full border border-stone-300 flex items-center justify-center text-[9px] text-stone-400 flex-shrink-0">{idx + 1}</div>
                <span className="flex-1">{item.title}</span>
                <span className={`px-1.5 py-0.5 text-[9px] rounded-full border ${PRIORITY_CONFIG[item.priority]?.color || "bg-stone-100 text-stone-500"}`}>
                  {item.priority}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button variant="ghost" size="sm" onClick={() => setShowApply(!showApply)} className="h-8 text-xs text-violet-600 hover:bg-violet-50" data-testid={`apply-template-btn-${template.id}`}>
            <Play className="w-3.5 h-3.5 mr-1" /> Apply
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onEdit(template)} className="h-8 w-8 p-0 text-stone-400 hover:text-stone-600">
            <Pencil className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete(template.id)} className="h-8 w-8 p-0 text-stone-400 hover:text-red-500">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {showApply && (
        <div className="mt-4 pt-4 border-t border-stone-100" data-testid="apply-template-panel">
          <p className="text-xs font-semibold text-stone-700 mb-2">Select stores to apply this template:</p>
          {stores.length === 0 ? (
            <p className="text-xs text-stone-400">No stores available. Create a store first.</p>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 mb-3">
                {stores.map(s => (
                  <button key={s.id} onClick={() => toggleStore(s.id)}
                    className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${
                      selectedStores.includes(s.id) ? "bg-violet-100 border-violet-300 text-violet-700" : "bg-stone-50 border-stone-200 text-stone-600 hover:bg-stone-100"
                    }`}
                    data-testid={`store-select-${s.id}`}
                  >
                    <Store className="w-3 h-3 inline mr-1" />{s.name}
                  </button>
                ))}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => { setShowApply(false); setSelectedStores([]); }} className="text-xs h-8">Cancel</Button>
                <Button size="sm" onClick={handleApply} disabled={applying || selectedStores.length === 0}
                  className="bg-violet-600 text-white hover:bg-violet-700 text-xs h-8" data-testid="confirm-apply-btn">
                  {applying ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Play className="w-3 h-3 mr-1" />}
                  Apply to {selectedStores.length} store{selectedStores.length !== 1 && "s"}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CreateTemplateForm({ categories, onSubmit, onCancel, editTemplate }) {
  const [name, setName] = useState(editTemplate?.name || "");
  const [description, setDescription] = useState(editTemplate?.description || "");
  const [recurring, setRecurring] = useState(editTemplate?.recurring || "daily");
  const [items, setItems] = useState(editTemplate?.items?.length ? editTemplate.items.map((item, i) => ({ ...item, _key: i })) : [{ _key: 0, title: "", description: "", priority: "medium", category_id: "" }]);
  const [submitting, setSubmitting] = useState(false);
  let keyCounter = items.length;

  const addItem = () => {
    setItems(prev => [...prev, { _key: ++keyCounter, title: "", description: "", priority: "medium", category_id: "" }]);
  };

  const removeItem = (key) => {
    if (items.length <= 1) return;
    setItems(prev => prev.filter(i => i._key !== key));
  };

  const updateItem = (key, field, value) => {
    setItems(prev => prev.map(i => i._key === key ? { ...i, [field]: value } : i));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Template name is required");
    const validItems = items.filter(i => i.title.trim());
    if (validItems.length === 0) return toast.error("Add at least one task item");
    setSubmitting(true);
    await onSubmit({
      name: name.trim(), description, recurring,
      items: validItems.map(({ _key, ...rest }) => ({ ...rest, category_id: rest.category_id || null })),
    });
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} data-testid="create-template-form" className="bg-white border border-stone-200/60 rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-stone-800">{editTemplate ? "Edit Template" : "New Template"}</h3>
        <button type="button" onClick={onCancel}><X className="w-4 h-4 text-stone-400" /></button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <label className="text-[11px] text-stone-500 font-medium mb-1 block">Template Name</label>
          <Input data-testid="template-name-input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Daily Opening Checklist" className="text-sm" />
        </div>
        <div>
          <label className="text-[11px] text-stone-500 font-medium mb-1 block">Recurrence</label>
          <select data-testid="template-recurring-select" value={recurring} onChange={e => setRecurring(e.target.value)}
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
      </div>

      <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Description (optional)" className="text-sm min-h-[50px]" />

      {/* Task Items */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-[11px] text-stone-500 font-medium">Task Items</label>
          <Button type="button" size="sm" variant="ghost" onClick={addItem} className="text-xs h-7"><Plus className="w-3 h-3 mr-1" /> Add Item</Button>
        </div>
        <div className="space-y-2">
          {items.map((item, idx) => (
            <div key={item._key} className="flex items-start gap-2 p-3 bg-stone-50 rounded-lg border border-stone-100">
              <span className="w-5 h-5 bg-stone-200 rounded-full flex items-center justify-center text-[10px] font-bold text-stone-500 flex-shrink-0 mt-1">{idx + 1}</span>
              <div className="flex-1 space-y-2">
                <Input value={item.title} onChange={e => updateItem(item._key, "title", e.target.value)}
                  placeholder="Task title" className="text-xs h-8" data-testid={`template-item-title-${idx}`} />
                <div className="flex gap-2">
                  <select value={item.priority} onChange={e => updateItem(item._key, "priority", e.target.value)}
                    className="border border-stone-200 rounded-lg px-2 py-1 text-xs bg-white flex-1">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                  {categories.length > 0 && (
                    <select value={item.category_id || ""} onChange={e => updateItem(item._key, "category_id", e.target.value)}
                      className="border border-stone-200 rounded-lg px-2 py-1 text-xs bg-white flex-1">
                      <option value="">No category</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  )}
                </div>
              </div>
              <button type="button" onClick={() => removeItem(item._key)} className="text-stone-400 hover:text-red-500 mt-1">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={submitting} className="bg-violet-600 text-white hover:bg-violet-700" data-testid="submit-template-btn">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileText className="w-4 h-4 mr-1" />}
          {editTemplate ? "Update Template" : "Create Template"}
        </Button>
      </div>
    </form>
  );
}

// --- Main Component ---

export default function Tasks() {
  const { isOwner, user } = useAuth();
  const [activeTab, setActiveTab] = useState("tasks"); // "tasks" or "templates"
  const [tasks, setTasks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stores, setStores] = useState([]);
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [viewMode, setViewMode] = useState("all");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [filterStore, setFilterStore] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [sortBy, setSortBy] = useState("created"); // "created", "due_date", "priority"

  const loadData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (viewMode === "mine") params.append("my_tasks", "true");
      if (filterStatus) params.append("status", filterStatus);
      if (filterPriority) params.append("priority", filterPriority);
      if (filterStore) params.append("store_id", filterStore);
      if (filterCategory) params.append("category_id", filterCategory);

      const [tasksRes, catsRes, storesRes, membersRes, statsRes, tmplRes] = await Promise.all([
        axios.get(`${API}/tasks?${params}`),
        axios.get(`${API}/tasks/categories`),
        axios.get(`${API}/stores`),
        axios.get(`${API}/team`),
        isOwner ? axios.get(`${API}/tasks/stats`) : Promise.resolve({ data: null }),
        axios.get(`${API}/tasks/templates`),
      ]);
      setTasks(tasksRes.data);
      setCategories(catsRes.data);
      setStores(storesRes.data);
      setMembers(membersRes.data);
      if (statsRes.data) setStats(statsRes.data);
      setTemplates(tmplRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [viewMode, filterStatus, filterPriority, filterStore, filterCategory, isOwner]);

  useEffect(() => { loadData(); }, [loadData]);

  const createTask = async (payload) => {
    try {
      await axios.post(`${API}/tasks`, payload);
      toast.success("Task created!");
      setShowCreate(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create task"); }
  };

  const editTask = async (payload) => {
    try {
      await axios.put(`${API}/tasks/${editingTask.id}`, payload);
      toast.success("Task updated!");
      setEditingTask(null);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update task"); }
  };

  const updateTask = async (id, updates) => {
    try { await axios.put(`${API}/tasks/${id}`, updates); loadData(); }
    catch (e) { toast.error("Failed to update task"); }
  };

  const deleteTask = async (id) => {
    try { await axios.delete(`${API}/tasks/${id}`); toast.success("Task deleted"); loadData(); }
    catch (e) { toast.error("Failed to delete task"); }
  };

  const completeTask = async (id) => {
    try { await axios.post(`${API}/tasks/${id}/complete`); toast.success("Task completed!"); loadData(); }
    catch (e) { toast.error("Failed to complete task"); }
  };

  const addNote = async (id, text) => {
    try { await axios.post(`${API}/tasks/${id}/notes`, { text }); toast.success("Note added"); loadData(); }
    catch (e) { toast.error("Failed to add note"); }
  };

  const addCategory = async (data) => {
    try { await axios.post(`${API}/tasks/categories`, data); toast.success("Category added"); loadData(); }
    catch (e) { toast.error("Failed to add category"); }
  };

  const deleteCategory = async (id) => {
    try { await axios.delete(`${API}/tasks/categories/${id}`); loadData(); }
    catch (e) { toast.error("Failed to delete category"); }
  };

  // Template actions
  const createTemplate = async (payload) => {
    try {
      await axios.post(`${API}/tasks/templates`, payload);
      toast.success("Template created!");
      setShowCreateTemplate(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create template"); }
  };

  const updateTemplate = async (payload) => {
    try {
      await axios.put(`${API}/tasks/templates/${editingTemplate.id}`, payload);
      toast.success("Template updated!");
      setEditingTemplate(null);
      setShowCreateTemplate(false);
      loadData();
    } catch (e) { toast.error("Failed to update template"); }
  };

  const deleteTemplate = async (id) => {
    try { await axios.delete(`${API}/tasks/templates/${id}`); toast.success("Template deleted"); loadData(); }
    catch (e) { toast.error("Failed to delete template"); }
  };

  const applyTemplate = async (templateId, storeIds) => {
    try {
      const res = await axios.post(`${API}/tasks/templates/${templateId}/apply`, { store_ids: storeIds });
      toast.success(res.data.message);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to apply template"); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
      </div>
    );
  }

  const PRIORITY_ORDER = { urgent: 0, high: 1, medium: 2, low: 3 };

  const sortTasks = (taskList) => {
    return [...taskList].sort((a, b) => {
      if (sortBy === "due_date") {
        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date) - new Date(b.due_date);
      }
      if (sortBy === "priority") {
        return (PRIORITY_ORDER[a.priority] ?? 2) - (PRIORITY_ORDER[b.priority] ?? 2);
      }
      return 0; // "created" keeps API default order
    });
  };

  const pendingTasks = sortTasks(tasks.filter(t => t.status === "pending"));
  const inProgressTasks = sortTasks(tasks.filter(t => t.status === "in_progress"));
  const completedTasks = sortTasks(tasks.filter(t => t.status === "completed"));

  return (
    <div className="px-4 sm:px-6 md:px-12 lg:px-16 py-6 sm:py-10 bg-stone-50 min-h-screen" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>Tasks</h1>
          <p className="text-sm text-stone-500 mt-1">{isOwner ? "Manage and track all team tasks" : "Your tasks and to-dos"}</p>
        </div>
        <div className="flex gap-2">
          {activeTab === "templates" ? (
            <Button data-testid="create-template-btn" onClick={() => { setShowCreateTemplate(true); setEditingTemplate(null); }}
              className="bg-violet-600 text-white hover:bg-violet-700 w-full sm:w-auto">
              <Plus className="w-4 h-4 mr-2" /> New Template
            </Button>
          ) : (
            <Button data-testid="create-task-btn" onClick={() => setShowCreate(true)} className="bg-stone-900 text-white hover:bg-stone-800 w-full sm:w-auto">
              <Plus className="w-4 h-4 mr-2" /> New Task
            </Button>
          )}
        </div>
      </div>

      {/* Tab Toggle */}
      <div className="flex gap-1 bg-stone-100 rounded-lg p-1 w-fit mb-6" data-testid="task-tabs">
        <button onClick={() => setActiveTab("tasks")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === "tasks" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-700"}`}
          data-testid="tab-tasks">
          <Target className="w-4 h-4 inline mr-1.5" />Tasks
        </button>
        <button onClick={() => setActiveTab("templates")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === "templates" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-700"}`}
          data-testid="tab-templates">
          <FileText className="w-4 h-4 inline mr-1.5" />Templates
          {templates.length > 0 && <span className="ml-1.5 text-xs bg-violet-100 text-violet-600 rounded-full px-1.5">{templates.length}</span>}
        </button>
      </div>

      {/* Templates Tab */}
      {activeTab === "templates" && (
        <div className="space-y-4">
          {(showCreateTemplate || editingTemplate) && (
            <CreateTemplateForm
              categories={categories}
              editTemplate={editingTemplate}
              onSubmit={editingTemplate ? updateTemplate : createTemplate}
              onCancel={() => { setShowCreateTemplate(false); setEditingTemplate(null); }}
            />
          )}

          {templates.length === 0 && !showCreateTemplate ? (
            <div className="text-center py-16 bg-white border border-stone-200/60 rounded-xl">
              <FileText className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-stone-700 mb-1">No templates yet</h3>
              <p className="text-sm text-stone-500 mb-4">Create reusable task templates for your store operations</p>
              <Button onClick={() => setShowCreateTemplate(true)} className="bg-violet-600 text-white hover:bg-violet-700">
                <Plus className="w-4 h-4 mr-2" /> Create Template
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {templates.map(t => (
                <TemplateCard key={t.id} template={t} stores={stores} onApply={applyTemplate}
                  onEdit={(tmpl) => { setEditingTemplate(tmpl); setShowCreateTemplate(true); }}
                  onDelete={deleteTemplate} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tasks Tab */}
      {activeTab === "tasks" && (
        <>
          {/* Admin Stats */}
          {isOwner && stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
              <StatCard label="Total Tasks" value={stats.total} icon={Target} />
              <StatCard label="Pending" value={stats.pending} icon={Circle} color="text-stone-400" />
              <StatCard label="In Progress" value={stats.in_progress} icon={Clock} color="text-blue-500" />
              <StatCard label="Completed" value={stats.completed} icon={CheckCircle2} color="text-emerald-500" />
              <StatCard label="Overdue" value={stats.overdue} icon={AlertTriangle} color="text-red-500" />
              <StatCard label="Completion" value={`${stats.completion_rate}%`} icon={TrendingUp} color="text-emerald-600" />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar */}
            <div className="space-y-4">
              <Card className="border-stone-200/60">
                <CardContent className="p-4 space-y-3">
                  <h3 className="text-sm font-semibold text-stone-800 flex items-center gap-2"><Filter className="w-4 h-4" /> Filters</h3>
                  <div className="flex gap-1">
                    <button data-testid="filter-all-tasks" onClick={() => setViewMode("all")}
                      className={`text-xs px-3 py-1.5 rounded-lg transition-all ${viewMode === "all" ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>
                      All Tasks
                    </button>
                    <button data-testid="filter-my-tasks" onClick={() => setViewMode("mine")}
                      className={`text-xs px-3 py-1.5 rounded-lg transition-all ${viewMode === "mine" ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>
                      My Tasks
                    </button>
                  </div>
                  <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="w-full border border-stone-200 rounded-lg px-3 py-1.5 text-xs bg-white">
                    <option value="">All Statuses</option><option value="pending">Pending</option><option value="in_progress">In Progress</option><option value="completed">Completed</option>
                  </select>
                  <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} className="w-full border border-stone-200 rounded-lg px-3 py-1.5 text-xs bg-white">
                    <option value="">All Priorities</option><option value="urgent">Urgent</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
                  </select>
                  {stores.length > 0 && (
                    <select value={filterStore} onChange={e => setFilterStore(e.target.value)} className="w-full border border-stone-200 rounded-lg px-3 py-1.5 text-xs bg-white">
                      <option value="">All Stores</option>
                      {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  )}
                  {categories.length > 0 && (
                    <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="w-full border border-stone-200 rounded-lg px-3 py-1.5 text-xs bg-white">
                      <option value="">All Categories</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  )}
                  {(filterStatus || filterPriority || filterStore || filterCategory) && (
                    <button onClick={() => { setFilterStatus(""); setFilterPriority(""); setFilterStore(""); setFilterCategory(""); }}
                      className="text-xs text-stone-500 hover:text-stone-700 underline">Clear filters</button>
                  )}
                  <div className="border-t border-stone-100 pt-3">
                    <h3 className="text-sm font-semibold text-stone-800 flex items-center gap-2 mb-2"><ArrowUpDown className="w-4 h-4" /> Sort By</h3>
                    <select data-testid="sort-by-select" value={sortBy} onChange={e => setSortBy(e.target.value)} className="w-full border border-stone-200 rounded-lg px-3 py-1.5 text-xs bg-white">
                      <option value="created">Date Created</option>
                      <option value="due_date">Due Date</option>
                      <option value="priority">Priority</option>
                    </select>
                  </div>
                </CardContent>
              </Card>
              <CategoryManager categories={categories} onAdd={addCategory} onDelete={deleteCategory} />
              {isOwner && stats && <MemberProgress stats={stats.member_stats} />}
            </div>

            {/* Main task list */}
            <div className="lg:col-span-3 space-y-4">
              {showCreate && (
                <CreateTaskForm stores={stores} members={members} categories={categories} onSubmit={createTask} onCancel={() => setShowCreate(false)} />
              )}
              {editingTask && !showCreate && (
                <CreateTaskForm stores={stores} members={members} categories={categories} onSubmit={editTask} onCancel={() => setEditingTask(null)} initialData={editingTask} />
              )}

              {tasks.length === 0 ? (
                <div className="text-center py-16 bg-white border border-stone-200/60 rounded-xl">
                  <Target className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-stone-700 mb-1">No tasks yet</h3>
                  <p className="text-sm text-stone-500 mb-4">Create your first task or apply a template</p>
                  <div className="flex gap-2 justify-center">
                    <Button onClick={() => setShowCreate(true)} className="bg-stone-900 text-white"><Plus className="w-4 h-4 mr-2" /> Create Task</Button>
                    {templates.length > 0 && (
                      <Button variant="outline" onClick={() => setActiveTab("templates")} className="border-violet-200 text-violet-600">
                        <FileText className="w-4 h-4 mr-2" /> Use Template
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {pendingTasks.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-stone-600 mb-3 flex items-center gap-2">
                        <Circle className="w-4 h-4 text-stone-400" /> To Do <span className="text-xs text-stone-400 font-normal">({pendingTasks.length})</span>
                      </h3>
                      <div className="space-y-2">
                        {pendingTasks.map(t => <TaskCard key={t.id} task={t} stores={stores} members={members} categories={categories}
                          onUpdate={updateTask} onDelete={deleteTask} onAddNote={addNote} onComplete={completeTask} isOwner={isOwner} onEdit={setEditingTask} />)}
                      </div>
                    </div>
                  )}
                  {inProgressTasks.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-stone-600 mb-3 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-blue-500" /> In Progress <span className="text-xs text-stone-400 font-normal">({inProgressTasks.length})</span>
                      </h3>
                      <div className="space-y-2">
                        {inProgressTasks.map(t => <TaskCard key={t.id} task={t} stores={stores} members={members} categories={categories}
                          onUpdate={updateTask} onDelete={deleteTask} onAddNote={addNote} onComplete={completeTask} isOwner={isOwner} onEdit={setEditingTask} />)}
                      </div>
                    </div>
                  )}
                  {completedTasks.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-stone-600 mb-3 flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Completed <span className="text-xs text-stone-400 font-normal">({completedTasks.length})</span>
                      </h3>
                      <div className="space-y-2">
                        {completedTasks.map(t => <TaskCard key={t.id} task={t} stores={stores} members={members} categories={categories}
                          onUpdate={updateTask} onDelete={deleteTask} onAddNote={addNote} onComplete={completeTask} isOwner={isOwner} onEdit={setEditingTask} />)}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
